import {
  useFanGetSubmissionsQuery,
  useOnFanEngagementSubmissionsChangedSubscription,
  useOnMockFanEngagementSubmissionsChangedSubscription,
  FanGetSubmissionsQuery,
  FanGetSubmissionsDocument,
} from '@/generated/graphql';
import { createLogger } from '@/utils/createLogger';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';

const logger = createLogger('useFanEngagementSubmissions');

/**
 * Fetches the submissions for an engagement and updates the cache
 * when the submissions change via a subscription.
 */
export function useFanEngagementSubmissions({
  engagementId,
}: {
  engagementId: string;
}) {
  const { data, loading, error } = useFanGetSubmissionsQuery({
    variables: { engagementId },
    onError: (error) => {
      logger.error('Error fetching submissions', error);
    },
    onCompleted: () => {
      logger.info('Submissions fetched');
    },
  });

  const mockRealtime = useMockRealtimeChannel();

  useOnFanEngagementSubmissionsChangedSubscription({
    variables: { engagementId },
    onData: ({ client, data }) => {
      logger.info('Submissions changed', data);
      const nextSubmissions =
        data?.data?.engagementSubmissionsChanged?.submissions;
      if (!nextSubmissions) {
        logger.error('No submissions found');
        return;
      }

      logger.info('Updating cache with new submissions');
      logger.debug('nextSubmissions', nextSubmissions);
      client.cache.updateQuery<FanGetSubmissionsQuery>(
        {
          query: FanGetSubmissionsDocument,
          variables: { engagementId },
        },
        () => ({
          submissions: nextSubmissions,
        }),
      );
    },
    onError: (error) => {
      logger.error('subscription error', error);
    },
  });
  useOnMockFanEngagementSubmissionsChangedSubscription({
    skip: !mockRealtime,
    variables: { engagementId },
    onData: ({ client, data }) => {
      const nextSubmissions =
        data?.data?.mockEngagementSubmissionsChanged?.submissions;
      if (!nextSubmissions) {
        logger.error('No submissions found');
        return;
      }
      client.cache.updateQuery<FanGetSubmissionsQuery>(
        {
          query: FanGetSubmissionsDocument,
          variables: { engagementId },
        },
        () => ({
          submissions: nextSubmissions,
        }),
      );
    },
    onError: (error) => {
      logger.error('subscription error', error);
    },
  });

  return { data, loading, error };
}
